import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// The Assistant bar (Linear MAG-130). The bundle itself is assistant-service's
// /dock.js, loaded in public/index.html; this file wraps <main> so the bar can
// float inside the pane, and shares its mode with the sidebar item. The element
// owns the mode (closed · bar · open) and remembers it per browser; the sidebar
// toggles closed ↔ bar by writing the attribute. Admins only for now — the
// service enforces it, the UI just doesn't offer it.

const ASSISTANT_URL = process.env.REACT_APP_ASSISTANT_URL || '';
const ASSISTANT_KEY = process.env.REACT_APP_ASSISTANT_API_KEY || '';

type Mode = 'closed' | 'bar' | 'open';
type Ctx = { available: boolean; mode: Mode; toggle: () => void; online: boolean | null; busy: boolean };
const AssistantContext = createContext<Ctx>({ available: false, mode: 'closed', toggle: () => {}, online: null, busy: false });

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const available = !!ASSISTANT_URL && user?.role === 'admin';
  const [mode, setMode] = useState<Mode>('closed');
  const [online, setOnline] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const toggle = useCallback(() => {
    const el = document.querySelector('assistant-dock');
    el?.setAttribute('mode', mode === 'closed' ? 'bar' : 'closed');
  }, [mode]);
  useEffect(() => {
    const onStatus = (e: Event) => { const d = (e as CustomEvent).detail; setOnline(d.online); setBusy(d.busy); };
    const onMode = (e: Event) => setMode((e as CustomEvent).detail.mode);
    window.addEventListener('assistant-status', onStatus);
    window.addEventListener('assistant-mode', onMode);
    return () => { window.removeEventListener('assistant-status', onStatus); window.removeEventListener('assistant-mode', onMode); };
  }, []);
  const value = useMemo(() => ({ available, mode: available ? mode : 'closed' as Mode, toggle, online, busy }), [available, mode, toggle, online, busy]);
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export const useAssistant = () => useContext(AssistantContext);

/** <main> plus the floating bar. The wrapper is the bar's positioning box. */
export const AssistantPane: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { available, mode } = useAssistant();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  // The element's events don't bubble out of the shadow root on their own:
  // re-dispatch them on window for the provider.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const relay = (e: Event) => window.dispatchEvent(new CustomEvent(e.type, { detail: (e as CustomEvent).detail }));
    el.addEventListener('assistant-status', relay);
    el.addEventListener('assistant-mode', relay);
    return () => { el.removeEventListener('assistant-status', relay); el.removeEventListener('assistant-mode', relay); };
  }, [available]);
  return (
    <div className="relative flex-1 min-w-0 flex flex-col">
      <main className={`flex-1 overflow-y-auto bg-warm-white ${mode === 'closed' ? '' : 'pb-28'}`}>{children}</main>
      {available && user && (
        <assistant-dock ref={ref as any} api={ASSISTANT_URL} api-key={ASSISTANT_KEY} user-id={user.id} surface="marketing-dashboard" route={pathname} />
      )}
    </div>
  );
};
